import { Formik, FormikHelpers,FormikProps } from 'formik';
import React from 'react';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Location } from '~commonInfra/utils/Location';
import { incrementRouterSpaceReactKey } from '~src/spacesPlatform/actions/IncrementRouterSpaceReactKeyAction';

import { siteUrlsSelector } from '../../bootstrap/redux/selectors/SiteUrlsSelectors';
import { getAppStore } from '../../bootstrap/redux/store/AppStore';
import { Messages } from '../../codegen/Localize';
import { FormikTextBox } from '../../commonInfra/components/FormikField';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { is3rdParty } from '../../settings/siteAuthSettings/services/SiteAuthenticationSettingsService';
import { EmailValidationHelper } from '../../userManagement/helpers/EmailValidationHelper';
import { IServerUserInfo } from '../../userManagement/users/models/IServerUserInfo';
import { ISiteUserInfo } from '../../userManagement/users/models/ISiteUserInfo';
import { UserSettingsNotification,UserSettingsNotificationParams, UserSettingsNotificationType } from '../notifications/UserSettingsNotification';
import { UserSettings } from '../services/resources/UserSettings';
import {textFieldWidget } from '../styles/EmailAndDisplayNameFormStyles';
import { UserSettingsStyle } from '../styles/UserSettingsStyle';
import { GeneralAuthSettingsProps } from './GeneralAuthSettings';
import { InlineCancelAndSaveButtons } from './UserSettingEditButtons';
export namespace EmailAndDisplayNameForm {
  const MAX_CHARACTER_LIMIT = 100;

  function getIsSAMLUser(siteUser: ISiteUserInfo): boolean {
    return !!siteUser && !!siteUser.authenticationProviderId && is3rdParty(siteUser.authenticationProviderId);
  }

  export interface FormFields {
    displayName: string;
    email: string;
  }

  export type Props = Omit<GeneralAuthSettingsProps, 'emailFormInitialValues'> & {
    initialValues: FormFields;
    handleSubmit: (values: FormFields, formik: FormikHelpers<FormFields>, props: Pick<Props, 'initialValues'| 'siteUser' | 'serverUser'>) => void;
  }

  export function validateDisplayName(value: string) {
    if (!value || value.length > MAX_CHARACTER_LIMIT) {
      return Messages.userSettings_invalidDisplayNameMessage();
    }
  }

  export function validateEmail(value: string, isLocalAuth: boolean) {
    if(isLocalAuth && !value) return;
    if (!value || value.length > MAX_CHARACTER_LIMIT || !EmailValidationHelper.isValidEmail(value)) {
      return Messages.userSettings_invalidEmailMessage();
    }
  }

  export async function handleSubmit(values: FormFields, formik: FormikHelpers<FormFields>, props: Pick<Props, 'initialValues'| 'siteUser' | 'serverUser'>) {
    try {
      const serverUser: IServerUserInfo = props.serverUser;
      const { siteUser } = props;
      const isSAMLUser = getIsSAMLUser(siteUser);

      // display name
      const updatePromises = [];

      if (props.initialValues.displayName !== values.displayName) {
        // update displayname promise
        const updateDisplayNamePromise = UserSettings.updateUserDisplayName(serverUser.id, values.displayName)
          .then((result: IResult) => {
            // toasts
            const userSettingsNotificationParams: UserSettingsNotificationParams = {
              result: result,
              displayName: values.displayName,
              username: serverUser.username,
              userSettingsNotificationType: UserSettingsNotificationType.UpdateUserDisplayName
            };
            UserSettingsNotification.notify(userSettingsNotificationParams);
            return values.displayName;
          });
        updatePromises.push(updateDisplayNamePromise);
      }
      if (props.initialValues.email !== values.email) {
        const updateEmailFunction = isSAMLUser ? UserSettings.updateUserEmailAndDelay.bind(UserSettings) : UserSettings.updateUserEmail.bind(UserSettings);
        const updateEmailPromise = updateEmailFunction(serverUser.id, values.email)
          .then((result: IResult) => {
            // toasts
            const userSettingsNotificationParams: UserSettingsNotificationParams = {
              result: result,
              email: values.email,
              username: serverUser.username,
              userSettingsNotificationType: UserSettingsNotificationType.UpdateUserEmail
            };
            UserSettingsNotification.notify(userSettingsNotificationParams);
            return values.email;
          });
        updatePromises.push(updateEmailPromise);
      }

      const results = await Promise.all(updatePromises);
      const userNameIsSameAsEmail = getIsSAMLUser(siteUser);
      if (userNameIsSameAsEmail) {
        // If username is tied to email address, then navigate to new user url
        const urls = siteUrlsSelector(getAppStore().getState());
        Location.instance.setUrl(urls.userSettingsUrl(serverUser.username, serverUser.domainName));
      } else {
        getAppStore().dispatch(incrementRouterSpaceReactKey());
      }
    } catch (e) {
      ToasterService.instance.pop({ type:  ToastType.Error, text: Messages.errorUnknown()});
    } finally {
      formik.resetForm();
    }
  }

  function showButtons(formik: FormikProps<FormFields>, props: Props): boolean {
    if (!formik.dirty || !formik.isValid || formik.isSubmitting || (formik.values.email.trim() === props.initialValues.email.trim() && formik.values.displayName.trim() === props.initialValues.displayName.trim())) {
      return false;
    }
    return true;
  }

  export function Form({initialValues: { displayName = '', email = ''}, ...otherProps}: Props) {
    const props: Props = {initialValues: {displayName, email}, ...otherProps};
    const siteUser: ISiteUserInfo = props.siteUser;
    const isSAMLUser = getIsSAMLUser(siteUser);

    const validateEmailCallback = React.useCallback(
      (value: string) => validateEmail(value, props.isDomainFamilyLocal),
      [props.isDomainFamilyLocal]
    );

    return (
      <Formik
        initialValues={{ ...props.initialValues }}
        onSubmit={function(values: FormFields, formik: FormikHelpers<FormFields>) {
          const {siteUser, serverUser, initialValues} = props;
          props.handleSubmit(values, formik, {siteUser, serverUser, initialValues});
        }}
      >
        {(formik: FormikProps<FormFields>) => (
          <form onSubmit={formik.handleSubmit}>
            <table>
              <tbody className={UserSettingsStyle.tbody}>
                {/* Display Name Section */}
                {(props.isDomainFamilyAD || props.isViewerServerAdmin || props.isViewerSelf) &&
                  <tr data-tb-test-id="displayname-section">
                    <td className={UserSettingsStyle.labelText}>
                      {Messages.displayName_title_case()}
                    </td>
                    <td className="tb-details-value">
                      {!props.updateDisplayNameAllowed && props.initialValues.displayName}
                      {props.updateDisplayNameAllowed &&
                        <FormikTextBox
                          name="displayName"
                          testId="displayName"
                          label=""
                          validate={validateDisplayName}
                          className={textFieldWidget}
                          showValidationMessage={false}
                        />
                      }
                    </td>
                  </tr>
                }
                {/* Email Section */}
                {(props.isDomainFamilyLocal || props.isDomainFamilyAD || isSAMLUser && props.isViewerServerAdmin) &&
                  <tr data-tb-test-id="email-section">
                    <td className={UserSettingsStyle.labelText}>{Messages.email()}</td>
                    <td className="tb-details-value">
                      <FormikTextBox
                        name="email"
                        testId="email"
                        label=""
                        validate={validateEmailCallback}
                        className={textFieldWidget}
                        showValidationMessage={false}
                      />
                    </td>
                  </tr>
                }
                {showButtons(formik, props) &&
                  <InlineCancelAndSaveButtons
                  testId={'edit-email'}
                  formik={formik}
                  />
                }
                {props.isDomainFamilyExternal && !(isSAMLUser && props.isViewerServerAdmin) &&
                  <tr data-test-id="email-value-section">
                    <td className={UserSettingsStyle.labelText}>{Messages.email()}</td>
                    <td className="tb-details-value" tb-test-id="email">{props.initialValues.email}</td>
                  </tr>
                }
              </tbody>
            </table>
            <div>
            </div>
          </form>
        )}
      </Formik>
    );
  }
}
